import React, { useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { TwoFactorAuthSettings } from '../../components/TwoFactorAuthSettings';
import { TemporaryTwoFADisable } from '../../components/TemporaryTwoFADisable';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Users, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SuperadminSettings = () => {
  const { user } = useAuth();
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handlePasswordReset = async () => {
    setMigrationLoading(true);
    setMigrationResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/superadmin/maintenance/reset-employee-passwords`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMigrationResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen der Passwörter');
    } finally {
      setMigrationLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Sicherheitseinstellungen
          </h1>
          <p className="text-muted-foreground mt-2">
            Verwalten Sie Ihre Superadmin-Sicherheitseinstellungen
          </p>
        </div>

        {/* Temporary 2FA Disable - Support Feature */}
        <TemporaryTwoFADisable user={user} />

        {/* Normal 2FA Settings */}
        <TwoFactorAuthSettings user={user} />

        {/* ===== WARTUNG / MAINTENANCE ===== */}
        <Card className="border-2 border-orange-200 dark:border-orange-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <AlertTriangle className="h-5 w-5" />
              Wartung &amp; Daten-Migration
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Einmalige Server-Migrationen für Produktionsdaten
            </p>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Migration Block */}
            <div className="border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-3 bg-orange-50 dark:bg-orange-950/20">
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    Mitarbeiter-Logins reparieren (Reset auf 1234)
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Setzt alle Mitarbeiter- &amp; Stationsleitungs-Passwörter auf <strong>1234</strong> zurück
                    und erzwingt eine Passwortänderung beim nächsten Login.
                    Nützlich nach Server-Deployments oder wenn Mitarbeiter sich nicht einloggen können.
                  </p>
                </div>
              </div>

              {/* Ergebnis-Anzeige */}
              {migrationResult && (
                <div className="flex items-start gap-2 bg-green-50 dark:bg-green-950/30 border border-green-300 dark:border-green-700 rounded-lg p-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-800 dark:text-green-200 font-medium" data-testid="migration-result">
                    {migrationResult.message}
                  </p>
                </div>
              )}

              {/* Bestätigungs-Bereich */}
              {!showConfirm ? (
                <Button
                  variant="outline"
                  className="border-orange-400 text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                  onClick={() => { setShowConfirm(true); setMigrationResult(null); }}
                  data-testid="reset-passwords-btn"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Mitarbeiter-Logins reparieren
                </Button>
              ) : (
                <div className="space-y-3 border border-red-300 dark:border-red-700 rounded-lg p-4 bg-red-50 dark:bg-red-950/20">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800 dark:text-red-200 font-semibold">
                      ⚠️ Sicherheitsabfrage: Wirklich alle Mitarbeiter-Passwörter auf "1234" zurücksetzen?
                    </p>
                  </div>
                  <p className="text-xs text-red-700 dark:text-red-300 pl-7">
                    Dies betrifft ALLE Mitarbeiter und Stationsleitungen im System.
                    Jeder muss beim nächsten Login ein neues Passwort setzen.
                  </p>
                  <div className="flex gap-3 pl-7">
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={handlePasswordReset}
                      disabled={migrationLoading}
                      data-testid="confirm-reset-btn"
                    >
                      {migrationLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird ausgeführt...</>
                      ) : (
                        'Ja, jetzt zurücksetzen'
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowConfirm(false)}
                      disabled={migrationLoading}
                      data-testid="cancel-reset-btn"
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>

          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
};
